import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup, FormControl, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  host: { 'class': 'w-full block' }
})
export class SignUpComponent {
  private authService = inject(AuthService);
  private router = inject(Router);

  signUpForm = new FormGroup({
    username: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    email: new FormControl('', { validators: [Validators.required, Validators.email], nonNullable: true }),
    password: new FormControl('', { validators: [Validators.required, Validators.minLength(8)], nonNullable: true }),
    confirmPassword: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    acceptTerms: new FormControl(false, { validators: [Validators.requiredTrue], nonNullable: true })
  }, { validators: [this.passwordMatchValidator] });

  isLoading = false;
  errorMessage = '';
  passwordVisible = false;
  confirmPasswordVisible = false;

  passwordMatchValidator(g: AbstractControl) {
    return g.get('password')?.value === g.get('confirmPassword')?.value
      ? null : { mismatch: true };
  }

  togglePasswordVisibility(field: 'password' | 'confirm') {
    if (field === 'password') this.passwordVisible = !this.passwordVisible;
    if (field === 'confirm') this.confirmPasswordVisible = !this.confirmPasswordVisible;
  }

  onSubmit() {
    if (this.signUpForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';
      const { username, email, password } = this.signUpForm.value;
      const normalizedEmail = email!.toLowerCase();
      
      this.authService.signup({ username: username!, email: normalizedEmail, password: password! }).subscribe({
        next: () => {
          // After successful sign up, redirect to check-email to let user know they need to activate
          this.router.navigate(['/auth/check-email'], { 
            queryParams: { type: 'signup', email: normalizedEmail } 
          });
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading = false;
          // Format errors from Djoser: often an object with keys mapped to fields e.g. {username: ["username already exists."]}
          let errorText = 'Failed to sign up.';
          if (err.error) {
             const keys = Object.keys(err.error);
             if (keys.length > 0 && Array.isArray(err.error[keys[0]])) {
                errorText = err.error[keys[0]][0];
             }
          }
          this.errorMessage = errorText;
        }
      });
    } else {
      this.signUpForm.markAllAsTouched();
    }
  }
}
